import { translateBackend } from '@assets/i18n/config';
import type { CustomField } from '@interfaces/customField';
import type { User } from '@interfaces/user';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';
import {
  CharityRegistrationNumber,
  FieldComponent,
  InsiderSymbols,
  PoiUserField,
  RegulatedEntityDetails,
  ReportingIssuerDetails,
  SSSymbolsField,
} from './components/personsOfInterestFields';

export type UserWithCustomFields = Partial<User> & { customFields?: any[] };

export const PersonsOfInterestVisual = ({
  options,
  userData,
  loading,
  updateUser,
  continueFunc,
  activeCustomFields,
  derivedCustomFields,
  workflowCompletion,
  grid = false,
  updateMode = false,
  ignoreCustomFieldTriggerRules,
  defaultOptionsToUndefined,
}: {
  options: any;
  userData: UserWithCustomFields;
  loading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  workflowCompletion?: any;
  updateUser: (user: UserWithCustomFields) => void;
  continueFunc: () => void;
  ignoreCustomFieldTriggerRules?: boolean;
  defaultOptionsToUndefined?: boolean;
}) => {
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation('client');
  const [updated, setUpdated] = useState(false);
  const [focused, setFocused] = useState<string[]>([]);
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: userData.customFields,
    });

  const update = (newValue: any) => {
    setUpdated(true);
    updateUser(newValue);
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const fields = invalidFields(
    {
      ...options,
      ssSymbols: {
        ...options.ssSymbols,
        requiredIf: (data: any) => data?.isOwnerOfPublicCompany,
      },
      insiderSymbols: {
        ...options.insiderSymbols,
        requiredIf: (data: any) => data?.isMemberOfIiroc,
      },
      regulatorName: {
        ...options.regulatorName,
        requiredIf: (data: any) => data?.isEntityRegulated,
      },
      firmNumber: {
        ...options.firmNumber,
        requiredIf: (data: any) => data?.isEntityRegulated,
      },
      issuerName: {
        ...options.issuerName,
        requiredIf: (data: any) => data?.isReportingIssuer,
      },
      issuerExchange: {
        ...options.issuerExchange,
        requiredIf: (data: any) => data?.isReportingIssuer,
      },
      issuerTicker: {
        ...options.issuerTicker,
        requiredIf: (data: any) => data?.isReportingIssuer,
      },
      isRegisteredWithCRA: {
        ...options.isRegisteredWithCRA,
        requiredIf: (data: any) => data?.isNonProfit,
      },
      charityRegistrationNumber: {
        ...options.charityRegistrationNumber,
        requiredIf: (data: any) => data?.isRegisteredWithCRA,
      },
      receivesDonationsFromPublic: {
        ...options.receivesDonationsFromPublic,
        requiredIf: (data: any) => data?.isNonProfit,
      },
      otherPersonGuaranteeNames: {
        ...options.otherPersonGuaranteeNames,
        requiredIf: (data: any) => data?.hasGuarantor,
      },
      tradingAuthorityName: {
        ...options.tradingAuthorityName,
        requiredIf: (data: any) => data?.tradingAuthorityOverAccountGranted,
      },
      advisorInterestDetail: {
        ...options.advisorInterestDetail,
        requiredIf: (data: any) => data?.advisorInterest,
      },
      investmentFirmTradeName: {
        ...options.investmentFirmTradeName,
        requiredIf: (data: any) => data?.investmentFirmTrade,
      },
      thirdPartyName: {
        ...options.thirdPartyName,
        requiredIf: (data: any) => data?.isForThirdParty,
      },
      pepReason: {
        ...options.pepReason,
        requiredIf: (data: any) =>
          data?.politicallyExposedDomesticPerson ||
          data?.politicallyExposedForeignPerson ||
          data?.closeAssociateOfPEP ||
          data?.headOfInternationalOrganization,
      },
      discretionOverAccountGrantedReason: {
        ...options.discretionOverAccountGrantedReason,
        requiredIf: (data: any) => data?.discretionOverAccountGranted,
      },
    },
    userData,
  );
  const isInvalid = fields.length > 0;

  const submit = async () => {
    const invalidCustomFieldKeys = validateAllCustomFields();
    const allInvalidFields = [...fields, ...invalidCustomFieldKeys];
    setFocused(allInvalidFields);

    if (allInvalidFields.length === 0) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsPersonOfInterestContinueButtonSelect,
        {
          workflowStepTitle: options?.title,
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );
      await continueFunc();
      setUpdated(false);
    }
  };

  return (
    <WorkflowCompletionForm
      testId={'poi-form'}
      onSubmit={submit}
      showSubmitButton={!updateMode || options.displayUpdateButton}
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: loading || (!updated && updateMode) || isInvalid,
        dataTestId: `persons-of-interest-${kebabCase(translateBackend(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options?.title)}
          {additionalInfo(options?.title?.additionalInfo) && (
            <InfoDialog
              information={additionalInfo(options?.title?.additionalInfo) ?? ''}
            />
          )}
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          display='inline-flex'
          alignItems='end'
          variant='bodyLarge'
          component='div'
          sx={{ mb: 3, table: { width: '100%' } }}
        >
          <Box display='inline-block'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(options?.subtitle?.additionalInfo) && (
            <InfoDialog
              information={
                additionalInfo(options?.subtitle?.additionalInfo) ?? ''
              }
            />
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isEntityRegulated'
          nestedElements={['regulatorName', 'firmNumber']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <RegulatedEntityDetails
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isReportingIssuer'
          nestedElements={['issuerName', 'issuerExchange', 'issuerTicker']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <ReportingIssuerDetails
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isNonProfit'
          nestedElements={[
            'charityRegistrationNumber',
            'isRegisteredWithCRA',
            'receivesDonationsFromPublic',
          ]}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          {userData.isNonProfit && (
            <FieldComponent
              options={options}
              grid={grid}
              userData={userData}
              loading={loading}
              update={update}
              t={t}
              field='isRegisteredWithCRA'
              noGrid
              nestedElements={['charityRegistrationNumber']}
            />
          )}
          {userData.isNonProfit && (
            <CharityRegistrationNumber
              update={update}
              userData={userData}
              loading={loading}
              options={options}
              setFocused={setFocused}
              focused={focused}
            />
          )}
          {userData.isNonProfit && (
            <FieldComponent
              options={options}
              grid={grid}
              userData={userData}
              loading={loading}
              update={update}
              t={t}
              field='receivesDonationsFromPublic'
              noGrid
            />
          )}
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isForThirdParty'
          nestedElements={['thirdPartyName']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='isForThirdParty'
            conditionToRender={
              options?.thirdPartyName?.enabled && userData.isForThirdParty
            }
            field={'thirdPartyName'}
            testId={'third-party-name'}
            focusValue={'thirdPartyName'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isAFinancialEntity'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isAffiliateOfFinancialEntity'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='instructionsFromFinancialEntity'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isPublicBody'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isLargeCorporation'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='powerOfAttorneyGranted'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='headOfInternationalOrganization'
          nestedElements={['pepReason']}
          conditionToClear={
            !userData.politicallyExposedDomesticPerson &&
            !userData.politicallyExposedForeignPerson &&
            !userData.closeAssociateOfPEP
          }
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='headOfInternationalOrganization'
            conditionToRender={
              options?.pepReason?.enabled &&
              userData.headOfInternationalOrganization
            }
            field={'pepReason'}
            testId={'pep-reason'}
            focusValue={'pepReason'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isOfficerOfPublicCompany'
          nestedElements={['ssSymbols']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          {options?.ssSymbols?.enabled && userData.isOfficerOfPublicCompany && (
            <SSSymbolsField
              update={update}
              userData={userData}
              loading={loading}
              options={options}
              setFocused={setFocused}
              focused={focused}
            />
          )}
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isOwnerOfPublicCompany'
          nestedElements={['ssSymbols']}
          conditionToClear={!userData.isOfficerOfPublicCompany}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          {options?.ssSymbols?.enabled &&
            userData.isOwnerOfPublicCompany &&
            !userData.isOfficerOfPublicCompany && (
              <SSSymbolsField
                update={update}
                userData={userData}
                loading={loading}
                options={options}
                setFocused={setFocused}
                focused={focused}
              />
            )}
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='isMemberOfIiroc'
          nestedElements={['insiderSymbols']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <InsiderSymbols
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
          />
          {userData.isMemberOfIiroc === true &&
            options?.memberOfIirocWarningEnable === true && (
              <Alert severity={'warning'} sx={{ my: 2 }}>
                {translateBackend(options.memberOfIirocWarningText)}
              </Alert>
            )}
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='politicallyExposedForeignPerson'
          nestedElements={['pepReason']}
          conditionToClear={
            !userData.politicallyExposedDomesticPerson &&
            !userData.closeAssociateOfPEP &&
            !userData.headOfInternationalOrganization
          }
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='politicallyExposedForeignPerson'
            conditionToRender={
              options?.pepReason?.enabled &&
              userData.politicallyExposedForeignPerson &&
              !userData.headOfInternationalOrganization
            }
            field={'pepReason'}
            testId={'pep-reason'}
            focusValue={'pepReason'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='politicallyExposedDomesticPerson'
          nestedElements={['pepReason']}
          conditionToClear={
            !userData.politicallyExposedForeignPerson &&
            !userData.closeAssociateOfPEP &&
            !userData.headOfInternationalOrganization
          }
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='politicallyExposedDomesticPerson'
            conditionToRender={
              options?.pepReason?.enabled &&
              userData.politicallyExposedDomesticPerson &&
              !userData.politicallyExposedForeignPerson &&
              !userData.headOfInternationalOrganization
            }
            field={'pepReason'}
            testId={'pep-reason'}
            focusValue={'pepReason'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='closeAssociateOfPEP'
          nestedElements={['pepReason']}
          conditionToClear={
            !userData.politicallyExposedForeignPerson &&
            !userData.politicallyExposedDomesticPerson &&
            !userData.headOfInternationalOrganization
          }
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='closeAssociateOfPEP'
            conditionToRender={
              options?.pepReason?.enabled &&
              userData.closeAssociateOfPEP &&
              !userData.politicallyExposedForeignPerson &&
              !userData.politicallyExposedDomesticPerson &&
              !userData.headOfInternationalOrganization
            }
            field={'pepReason'}
            testId={'pep-reason'}
            focusValue={'pepReason'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='hasGuarantor'
          nestedElements={['otherPersonGuaranteeNames']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='hasGuarantor'
            conditionToRender={
              options?.otherPersonGuaranteeNames?.enabled &&
              userData.hasGuarantor
            }
            field={'otherPersonGuaranteeNames'}
            testId={'guarantee-name'}
            focusValue={'otherPersonGuaranteeNames'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='tradingAuthorityOverAccountGranted'
          nestedElements={['tradingAuthorityName']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='tradingAuthorityOverAccountGranted'
            conditionToRender={
              options?.tradingAuthorityName?.enabled &&
              userData.tradingAuthorityOverAccountGranted
            }
            field={'tradingAuthorityName'}
            testId={'trading-authority-name'}
            focusValue={'tradingAuthorityName'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='discretionOverAccountGranted'
          nestedElements={['discretionOverAccountGrantedReason']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='discretionOverAccountGranted'
            conditionToRender={
              options?.discretionOverAccountGrantedReason?.enabled &&
              userData.discretionOverAccountGranted
            }
            field={'discretionOverAccountGrantedReason'}
            testId={'discretion-over-account-granted-reason'}
            focusValue={'discretionOverAccountGrantedReason'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='advisorInterest'
          nestedElements={['advisorInterestDetail']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='advisorInterest'
            conditionToRender={
              options?.advisorInterestDetail?.enabled &&
              userData.advisorInterest
            }
            field={'advisorInterestDetail'}
            testId={'advisor-interest-detail'}
            focusValue={'advisorInterestDetail'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='investmentFirmTrade'
          nestedElements={['investmentFirmTradeName']}
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        >
          <PoiUserField
            update={update}
            userData={userData}
            loading={loading}
            options={options}
            setFocused={setFocused}
            focused={focused}
            parentField='investmentFirmTrade'
            conditionToRender={
              options?.investmentFirmTradeName?.enabled &&
              userData.investmentFirmTrade
            }
            field={'investmentFirmTradeName'}
            testId={'investment-firm-trade-name'}
            focusValue={'investmentFirmTradeName'}
          />
        </FieldComponent>

        <FieldComponent
          options={options}
          grid={grid}
          userData={userData}
          loading={loading}
          update={update}
          t={t}
          field='useBorrowedMoney'
          defaultOptionsToUndefined={defaultOptionsToUndefined}
        />

        <Grid item xs={12}>
          {renderCustomFields({
            customData: userData,
            update,
            focused,
            setFocused,
            loading,
            ignoreCustomFieldTriggerRules,
          })}
        </Grid>
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default PersonsOfInterestVisual;
